import React, { useState } from 'react';
import { 
  FileText, Search, Download, Eye, CheckCircle, XCircle,
  Clock, AlertTriangle, DollarSign, User, Calendar, MapPin,
  Building2, Pill, Activity
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Input';
import Select from '../../components/atoms/Select';
import StatCard from '../../components/molecules/StatCard';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { downloadSamplePDF } from '../../utils/pdfGenerator';

interface Claim {
  id: string;
  claimNumber: string;
  patientName: string;
  providerName: string;
  providerType: 'Hospital' | 'Clinic' | 'Pharmacy' | 'Lab';
  amount: number;
  submissionDate: string;
  processedDate?: string;
  status: 'pending' | 'approved' | 'rejected' | 'under-review' | 'frozen' | 'flagged';
  diagnosis: string;
  treatment: string;
  region: string;
  priority: 'normal' | 'urgent' | 'flagged';
  fwaScore?: number;
  automationDecision?: 'stp' | 'manual-review' | 'flagged';
}

const ClaimsManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'approved' | 'flagged'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);

  // Mock claims data
  const claims: Claim[] = [
    {
      id: '1',
      claimNumber: 'CLM-2025-10-001',
      patientName: 'Ahmed Mohammed Al-Hashimi',
      providerName: 'Dubai Medical Center',
      providerType: 'Hospital',
      amount: 12500,
      submissionDate: dayjs().subtract(2, 'hours').format('YYYY-MM-DD HH:mm'),
      status: 'pending',
      diagnosis: 'Acute Appendicitis',
      treatment: 'Emergency Appendectomy',
      region: 'Dubai',
      priority: 'urgent',
      automationDecision: 'manual-review'
    },
    {
      id: '2',
      claimNumber: 'CLM-2025-10-002',
      patientName: 'Fatima Ali Al-Mansoori',
      providerName: 'Al-Ain Hospital',
      providerType: 'Hospital',
      amount: 8750,
      submissionDate: dayjs().subtract(5, 'hours').format('YYYY-MM-DD HH:mm'),
      processedDate: dayjs().subtract(1, 'hour').format('YYYY-MM-DD HH:mm'),
      status: 'approved',
      diagnosis: 'Type 2 Diabetes Checkup',
      treatment: 'Blood Tests & Consultation',
      region: 'Al Ain',
      priority: 'normal',
      automationDecision: 'stp'
    },
    {
      id: '3',
      claimNumber: 'CLM-2025-10-003',
      patientName: 'Omar Khalid Al-Zaabi',
      providerName: 'Dr. Sarah Johnson Clinic',
      providerType: 'Clinic',
      amount: 15200,
      submissionDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD HH:mm'),
      status: 'flagged',
      diagnosis: 'Chronic Back Pain',
      treatment: 'MRI Scan & Physical Therapy',
      region: 'Abu Dhabi',
      priority: 'flagged',
      fwaScore: 85,
      automationDecision: 'flagged'
    },
    {
      id: '4',
      claimNumber: 'CLM-2025-10-004',
      patientName: 'Mariam Hassan Al-Blooshi',
      providerName: 'Life Pharmacy - Marina',
      providerType: 'Pharmacy',
      amount: 2450,
      submissionDate: dayjs().subtract(3, 'hours').format('YYYY-MM-DD HH:mm'),
      processedDate: dayjs().subtract(2, 'hours').format('YYYY-MM-DD HH:mm'),
      status: 'approved',
      diagnosis: 'Hypertension',
      treatment: 'Blood Pressure Medication (90 days)',
      region: 'Dubai',
      priority: 'normal',
      automationDecision: 'stp'
    },
    {
      id: '5',
      claimNumber: 'CLM-2025-10-005',
      patientName: 'Salem Abdullah Al-Ketbi',
      providerName: 'Emirates Diagnostic Center',
      providerType: 'Lab',
      amount: 3800,
      submissionDate: dayjs().subtract(6, 'hours').format('YYYY-MM-DD HH:mm'),
      status: 'under-review',
      diagnosis: 'Routine Health Screening',
      treatment: 'Complete Blood Count & Lipid Panel',
      region: 'Sharjah',
      priority: 'normal',
      automationDecision: 'manual-review'
    },
    {
      id: '6',
      claimNumber: 'CLM-2025-10-006',
      patientName: 'Noura Mohammed Al-Shamsi',
      providerName: 'City Health Clinic',
      providerType: 'Clinic',
      amount: 5600,
      submissionDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD HH:mm'),
      processedDate: dayjs().subtract(12, 'hours').format('YYYY-MM-DD HH:mm'),
      status: 'approved',
      diagnosis: 'Seasonal Allergies',
      treatment: 'Allergy Testing & Medication',
      region: 'Ajman',
      priority: 'normal',
      automationDecision: 'stp'
    },
    {
      id: '7',
      claimNumber: 'CLM-2025-10-007',
      patientName: 'Rashid Hassan Al-Mazrouei',
      providerName: 'Dubai Medical Center',
      providerType: 'Hospital',
      amount: 22500,
      submissionDate: dayjs().subtract(2, 'days').format('YYYY-MM-DD HH:mm'),
      status: 'flagged',
      diagnosis: 'Cardiac Catheterization',
      treatment: 'Angioplasty with Stent Placement',
      region: 'Dubai',
      priority: 'flagged',
      fwaScore: 92,
      automationDecision: 'flagged'
    },
    {
      id: '8',
      claimNumber: 'CLM-2025-10-008',
      patientName: 'Latifa Saeed Al-Suwaidi',
      providerName: 'Sharjah Specialized Clinic',
      providerType: 'Clinic',
      amount: 4200,
      submissionDate: dayjs().subtract(4, 'hours').format('YYYY-MM-DD HH:mm'),
      status: 'pending',
      diagnosis: 'Migraine Headaches',
      treatment: 'Neurological Consultation',
      region: 'Sharjah',
      priority: 'normal',
      automationDecision: 'manual-review'
    }
  ];

  const getFilteredClaims = () => {
    let filtered = claims;

    // Filter by tab
    if (activeTab === 'pending') {
      filtered = filtered.filter(c => c.status === 'pending' || c.status === 'under-review');
    } else if (activeTab === 'approved') {
      filtered = filtered.filter(c => c.status === 'approved');
    } else if (activeTab === 'flagged') {
      filtered = filtered.filter(c => c.status === 'flagged' || c.priority === 'flagged');
    }

    // Filter by search
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.claimNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.providerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(c => c.status === selectedStatus);
    }

    // Filter by provider
    if (selectedProvider) {
      filtered = filtered.filter(c => c.providerType === selectedProvider);
    }

    return filtered;
  };

  const handleViewDetails = (claim: Claim) => {
    setSelectedClaim(claim);
    setShowDetailsModal(true);
  };

  const handleApproveClaim = (claim: Claim) => {
    toast.success(`Claim ${claim.claimNumber} approved successfully`);
  };

  const handleRejectClaim = (claim: Claim) => {
    toast.error(`Claim ${claim.claimNumber} rejected`);
  };

  const handleExportClaims = () => {
    downloadSamplePDF('Claims_Report', `Claims_Export_${dayjs().format('YYYY-MM-DD')}.pdf`);
    toast.success('Claims data exported successfully');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'under-review':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'frozen':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <Clock className="w-4 h-4 text-orange-600" />;
      case 'flagged':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const filteredClaims = getFilteredClaims();

  return (
    <DashboardLayout>
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Claims Management</h1>
            <p className="text-lg text-gray-600">
              {dayjs().format('dddd, MMMM D, YYYY')} • Manage and review all insurance claims
            </p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <StatCard
              title="Total Claims"
              value={claims.length}
              icon={FileText}
              iconBgColor="bg-[#0077B6]/10"
              iconColor="text-[#0077B6]"
              subtitle="All submissions"
            />
            <StatCard
              title="Pending Review"
              value={claims.filter(c => c.status === 'pending' || c.status === 'under-review').length}
              icon={Clock}
              iconBgColor="bg-yellow-100"
              iconColor="text-yellow-600"
              subtitle="Awaiting action"
            />
            <StatCard
              title="Approved"
              value={claims.filter(c => c.status === 'approved').length}
              icon={CheckCircle}
              iconBgColor="bg-green-100"
              iconColor="text-green-600"
              subtitle="Processed successfully"
            />
            <StatCard
              title="Flagged for Review"
              value={claims.filter(c => c.status === 'flagged').length}
              icon={AlertTriangle}
              iconBgColor="bg-red-100"
              iconColor="text-red-600"
              subtitle="Requires attention"
            />
          </div>

          {/* Total Amount Card */}
          <div className="bg-[#03045E] rounded-2xl shadow-lg p-6 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm mb-2">Total Claims Value</p>
                <p className="text-4xl font-bold">
                  AED {claims.reduce((sum, c) => sum + c.amount, 0).toLocaleString()}
                </p>
              </div>
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 px-2">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                  activeTab === 'all'
                    ? 'text-[#0077B6] border-b-2 border-[#0077B6]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  All Claims ({claims.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                  activeTab === 'pending'
                    ? 'text-[#0077B6] border-b-2 border-[#0077B6]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pending ({claims.filter(c => c.status === 'pending' || c.status === 'under-review').length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab('approved')}
                className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                  activeTab === 'approved'
                    ? 'text-[#0077B6] border-b-2 border-[#0077B6]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Approved ({claims.filter(c => c.status === 'approved').length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab('flagged')}
                className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                  activeTab === 'flagged'
                    ? 'text-[#0077B6] border-b-2 border-[#0077B6]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Flagged ({claims.filter(c => c.status === 'flagged').length})
                </div>
              </button>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                placeholder="Search by claim number, patient, or provider..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
              />
              <Select
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' },
                  { value: 'under-review', label: 'Under Review' },
                  { value: 'flagged', label: 'Flagged' },
                  { value: 'frozen', label: 'Frozen' }
                ]}
                value={selectedStatus}
                onChange={(value) => setSelectedStatus(value)}
                placeholder="Filter by status"
              />
              <Select
                options={[
                  { value: '', label: 'All Providers' },
                  { value: 'Hospital', label: 'Hospitals' },
                  { value: 'Clinic', label: 'Clinics' },
                  { value: 'Pharmacy', label: 'Pharmacies' },
                  { value: 'Lab', label: 'Laboratories' }
                ]}
                value={selectedProvider}
                onChange={(value) => setSelectedProvider(value)}
                placeholder="Filter by provider type"
              />
              <Button variant="outline" icon={Download} onClick={handleExportClaims}>
                Export Claims
              </Button>
            </div>
          </div>

          {/* Claims Table */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Claims List</h3>
            
            <div className="space-y-4">
              {filteredClaims.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No claims found matching your filters</p>
                </div>
              ) : (
                filteredClaims.map((claim) => (
                  <div
                    key={claim.id}
                    className={`border-2 rounded-xl p-6 hover:shadow-md transition-shadow ${
                      claim.priority === 'flagged' ? 'border-red-200 bg-red-50/30' :
                      claim.priority === 'urgent' ? 'border-yellow-200 bg-yellow-50/30' :
                      'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="font-bold text-gray-900 text-lg">{claim.claimNumber}</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(claim.status)}`}>
                            {claim.status.toUpperCase().replace('-', ' ')}
                          </span>
                          {getPriorityIcon(claim.priority)}
                          {claim.automationDecision === 'stp' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              <Activity className="w-3 h-3 mr-1" />
                              Auto-Approved
                            </span>
                          )}
                          {claim.fwaScore && claim.fwaScore > 80 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              FWA Risk: {claim.fwaScore}
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Patient</p>
                            <p className="font-semibold text-gray-900 flex items-center gap-1">
                              <User className="w-4 h-4 text-[#0077B6]" />
                              {claim.patientName}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Provider</p>
                            <p className="font-semibold text-gray-900 flex items-center gap-1">
                              <Building2 className="w-4 h-4 text-[#0077B6]" />
                              {claim.providerName}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Amount</p>
                            <p className="font-semibold text-[#03045E] flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              AED {claim.amount.toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Region</p>
                            <p className="font-semibold text-gray-900 flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-[#0077B6]" />
                              {claim.region}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Diagnosis</p>
                            <p className="text-sm text-gray-900">{claim.diagnosis}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Treatment</p>
                            <p className="text-sm text-gray-900">{claim.treatment}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Submitted: {dayjs(claim.submissionDate).format('MMM DD, YYYY HH:mm')}
                          </span>
                          {claim.processedDate && (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Processed: {dayjs(claim.processedDate).format('MMM DD, YYYY HH:mm')}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={Eye}
                          onClick={() => handleViewDetails(claim)}
                        >
                          View Details
                        </Button>
                        {(claim.status === 'pending' || claim.status === 'under-review') && (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              icon={CheckCircle}
                              onClick={() => handleApproveClaim(claim)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Approve
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              icon={XCircle}
                              onClick={() => handleRejectClaim(claim)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Claim Details Modal */}
      {showDetailsModal && selectedClaim && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0077B6]/10 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#0077B6]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Claim Details</h3>
                  <p className="text-sm text-gray-600">{selectedClaim.claimNumber}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Claim Summary */}
            <div className={`rounded-xl p-6 mb-6 border-2 ${
              selectedClaim.priority === 'flagged' 
                ? 'bg-red-50 border-red-200' 
                : 'bg-[#0077B6]/5 border-[#0077B6]/20'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(selectedClaim.status)}`}>
                  {selectedClaim.status.toUpperCase().replace('-', ' ')}
                </span>
                {selectedClaim.automationDecision && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-[#0077B6]/20 text-[#0077B6]">
                    {selectedClaim.automationDecision === 'stp' ? 'Auto-Processed (STP)' :
                     selectedClaim.automationDecision === 'manual-review' ? 'Manual Review Required' :
                     'Fraud Alert Triggered'}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                  <p className="text-3xl font-bold text-[#03045E]">AED {selectedClaim.amount.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Submission Date</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {dayjs(selectedClaim.submissionDate).format('MMM DD, YYYY HH:mm')}
                  </p>
                </div>
              </div>
            </div>

            {/* Patient Information */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#0077B6]" />
                Patient Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Patient Name</p>
                  <p className="font-semibold text-gray-900">{selectedClaim.patientName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Region</p>
                  <p className="font-semibold text-gray-900">{selectedClaim.region}</p>
                </div>
              </div>
            </div>

            {/* Provider Information */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0077B6]" />
                Provider Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Provider Name</p>
                  <p className="font-semibold text-gray-900">{selectedClaim.providerName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Provider Type</p>
                  <p className="font-semibold text-gray-900">{selectedClaim.providerType}</p>
                </div>
                {selectedClaim.fwaScore && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600 mb-2">FWA Risk Score</p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${
                            selectedClaim.fwaScore > 80 ? 'bg-red-600' :
                            selectedClaim.fwaScore > 60 ? 'bg-yellow-600' :
                            'bg-green-600'
                          }`}
                          style={{ width: `${selectedClaim.fwaScore}%` }}
                        ></div>
                      </div>
                      <span className="font-bold text-gray-900">{selectedClaim.fwaScore}/100</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Medical Information */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Pill className="w-5 h-5 text-[#0077B6]" />
                Medical Information
              </h4>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Diagnosis</p>
                  <p className="font-semibold text-gray-900">{selectedClaim.diagnosis}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Treatment Provided</p>
                  <p className="font-semibold text-gray-900">{selectedClaim.treatment}</p>
                </div>
              </div>
            </div>

            {/* Processing Timeline */}
            <div className="bg-[#0077B6]/5 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#0077B6]" />
                Processing Timeline
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#0077B6] rounded-full mt-2"></div>
                  <div>
                    <p className="font-semibold text-gray-900">Claim Submitted</p>
                    <p className="text-sm text-gray-600">
                      {dayjs(selectedClaim.submissionDate).format('MMM DD, YYYY HH:mm')}
                    </p>
                  </div>
                </div>
                {selectedClaim.processedDate && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <div>
                      <p className="font-semibold text-gray-900">Claim Processed</p>
                      <p className="text-sm text-gray-600">
                        {dayjs(selectedClaim.processedDate).format('MMM DD, YYYY HH:mm')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDetailsModal(false)} 
                className="flex-1"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  downloadSamplePDF('Claim_Details', `Claim_${selectedClaim.claimNumber}_${dayjs().format('YYYY-MM-DD')}.pdf`);
                  toast.success('Claim details exported successfully');
                }}
                icon={Download}
                className="flex-1 bg-[#0077B6] hover:bg-[#03045E]"
              >
                Export PDF
              </Button>
              {(selectedClaim.status === 'pending' || selectedClaim.status === 'under-review') && (
                <>
                  <Button 
                    onClick={() => {
                      handleApproveClaim(selectedClaim);
                      setShowDetailsModal(false);
                    }}
                    icon={CheckCircle}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Approve
                  </Button>
                  <Button 
                    variant="danger"
                    onClick={() => {
                      handleRejectClaim(selectedClaim);
                      setShowDetailsModal(false);
                    }}
                    icon={XCircle}
                    className="flex-1"
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ClaimsManagement;
